import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Star,
  MapPin,
  ExternalLink,
  TrendingUp,
  Target,
  DollarSign,
  Briefcase,
  Award,
  Eye,
  UserPlus,
  Layers,
  Radar as RadarIcon,
} from "lucide-react";
import { Linkedin } from "@/components/icons/linkedin";
import { Recommendation } from "@/types/recommendation";
import { Link } from "react-router";
import { ExternalLinkButton } from "@/components/ui/external-link-button";
import { FreelancerLinkedInButton } from "@/components/freelancers/painel/FreelancerLinkedInButton";

interface RecommendationListProps {
  recommendations: Recommendation[];
  vagaId: string;
}

// Função para gerar iniciais do nome
const getInitials = (name: string): string => {
  const words = name.trim().split(' ').filter(word => word.length > 0);
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};


// Função para obter cor do badge de nível
const getLevelBadgeColor = (level: string) => {
  switch (level) {
    case 'EXCELLENT':
      return 'bg-green-100 text-green-800 border-green-300';
    case 'GOOD':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'MODERATE':
      return 'bg-orange-100 text-orange-800 border-orange-300';
    case 'POOR':
      return 'bg-red-100 text-red-800 border-red-300';
    default:
      return 'bg-slate-100 text-slate-800 border-slate-300';
  }
};

// Função para obter texto do nível em português
const getLevelText = (level: string) => {
  switch (level) {
    case 'EXCELLENT':
      return 'Excelente';
    case 'GOOD':
      return 'Bom';
    case 'MODERATE':
      return 'Moderado';
    case 'POOR':
      return 'Fraco';
    default:
      return level;
  }
};

// Badge do Radar de Perfis: sinaliza marca manual de curadoria (indicação
// humana para a agência da vaga), transparente e nunca chamada de "boost"
// ou "score" na UI — o texto exposto é sempre o vocabulário de negócio.
function RadarIndicadoBadge() {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className="gap-1 bg-amber-50 text-amber-800 border-amber-300 shrink-0"
        >
          <RadarIcon className="h-3 w-3" />
          Indicado no radar
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        Marcado manualmente como indicado para esta agência no Radar de Perfis
      </TooltipContent>
    </Tooltip>
  );
}

export function RecommendationList({ recommendations, vagaId }: RecommendationListProps) {
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  const toggleExpand = (freelancerId: string) => {
    setExpandedCard(expandedCard === freelancerId ? null : freelancerId);
  };

  return (
    <TooltipProvider delayDuration={300}>
    <div className="space-y-4">
      {recommendations.map((recommendation) => {
        const { freelancer, scoring, match_explanation, rank } = recommendation;
        const isExpanded = expandedCard === freelancer.id;
        
        return (
          <Card 
            key={freelancer.id}
            className="border-slate-200 hover:shadow-md transition-all duration-200"
          >
            <CardContent className="p-6">
              <div className="flex gap-4">
                {/* Ranking Badge */}
                <div className="shrink-0">
                  <div className="relative">
                    <div className="absolute -top-2 -left-2 w-6 h-6 bg-linear-to-r from-blue-600 to-purple-600 text-white text-xs rounded-full flex items-center justify-center font-bold shadow-lg border-2 border-white">
                      {rank}
                    </div>
                    <Link
                      to={`/freelancers/${freelancer.id}?fromVaga=${vagaId}`}
                      className="block"
                    >
                      <Avatar className="cursor-pointer w-16 h-16 shadow-md border-2 border-white">
                        <AvatarFallback className="bg-linear-to-r from-blue-100 to-purple-100 text-blue-700 text-lg font-bold">
                          {getInitials(freelancer.nome)}
                        </AvatarFallback>
                      </Avatar>
                    </Link>
                  </div>
                </div>

                {/* Informações principais */}
                <div className="flex-1 min-w-0">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <Link
                        to={`/freelancers/${freelancer.id}?fromVaga=${vagaId}`}
                        className="block"
                      >
                        <h3 className="font-bold text-slate-900 text-lg leading-tight hover:text-blue-600 transition-colors">
                          {freelancer.nome}
                        </h3>
                      </Link>
                      {freelancer.cidade && (
                        <div className="flex items-center gap-1 mt-1 text-sm text-slate-600">
                          <MapPin className="h-3 w-3" />
                          {freelancer.cidade}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      {match_explanation.radar_indicado && <RadarIndicadoBadge />}
                      <Badge className={getLevelBadgeColor(scoring.recommendation_level)}>
                        {getLevelText(scoring.recommendation_level)}
                      </Badge>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-slate-900">
                          {Math.round(parseFloat(scoring.overall_score))}
                        </div>
                        <div className="text-xs text-slate-500">Score Geral</div>
                      </div>
                    </div>
                  </div>

                  {/* Skills e Experiência */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Briefcase className="h-4 w-4 text-purple-500" />
                        <span className="text-sm font-medium text-slate-700">Top Skills</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {freelancer.top_skills.slice(0, 3).map((skill, index) => (
                          <Badge key={index} variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                            {skill}
                          </Badge>
                        ))}
                        {freelancer.top_skills.length > 3 && (
                          <Badge variant="outline" className="text-xs bg-slate-50 text-slate-600">
                            +{freelancer.top_skills.length - 3}
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Award className="h-4 w-4 text-blue-500" />
                        <span className="text-sm text-slate-600">
                          {freelancer.experiencia_anos} ano{freelancer.experiencia_anos !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {freelancer.pretensao_salarial && (
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-green-500" />
                          <span className="text-sm text-slate-600">
                            R$ {freelancer.pretensao_salarial.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Breakdown de scores — sinais principais com peso > 0
                      no engine (cat .34, areas .28, exp .17, dom .11, val .10).
                      "Áreas" = label antigo "Skills" — engine usa overlap
                      categórico (`f.areas && vaga.areas`). Categoria é o
                      sinal de maior peso (.34) e estava faltando aqui. */}
                  <div className="grid grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
                    {[
                      { label: 'Categoria', value: scoring.breakdown.category_score, icon: Layers, color: 'text-indigo-600' },
                      { label: 'Áreas', value: scoring.breakdown.skills_match_score, icon: Target, color: 'text-purple-600' },
                      { label: 'Experiência', value: scoring.breakdown.experience_score, icon: Award, color: 'text-blue-600' },
                      { label: 'Domínio', value: scoring.breakdown.domain_score, icon: TrendingUp, color: 'text-orange-600' },
                      { label: 'Valor', value: scoring.breakdown.value_match_score, icon: DollarSign, color: 'text-green-600' },
                    ].map((item) => (
                      <div key={item.label} className="text-center">
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <item.icon className={`h-3 w-3 ${item.color}`} />
                          <span className="text-xs font-medium text-slate-600">{item.label}</span>
                        </div>
                        <div className="text-sm font-bold text-slate-900">
                          {Math.round(parseFloat(item.value))}%
                        </div>
                        <Progress
                          value={parseFloat(item.value)}
                          className="h-1.5 mt-1"
                        />
                      </div>
                    ))}
                  </div>

                  {/* Explicação expandível */}
                  {isExpanded && (
                    <div className="border-t border-slate-200 pt-4 mt-4 space-y-4">
                      {/* Pontos fortes e preocupações */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {match_explanation.strengths.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-2">
                              <Star className="h-4 w-4" />
                              Pontos Fortes
                            </h4>
                            <ul className="space-y-1">
                              {match_explanation.strengths.map((strength, index) => (
                                <li key={index} className="text-sm text-slate-600 flex items-start gap-2">
                                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 shrink-0" />
                                  {strength}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {match_explanation.concerns.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-orange-700 mb-2 flex items-center gap-2">
                              <TrendingUp className="h-4 w-4" />
                              Pontos de Atenção
                            </h4>
                            <ul className="space-y-1">
                              {match_explanation.concerns.map((concern, index) => (
                                <li key={index} className="text-sm text-slate-600 flex items-start gap-2">
                                  <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 shrink-0" />
                                  {concern}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      {/* Resumo */}
                      <div className="bg-slate-50 p-4 rounded-lg">
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Resumo da Análise</h4>
                        <p className="text-sm text-slate-600">{match_explanation.summary}</p>
                      </div>

                      {/* All skills */}
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Todas as Skills</h4>
                        <div className="flex flex-wrap gap-1">
                          {freelancer.top_skills.map((skill, index) => (
                            <Badge key={index} variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Footer com ações */}
                  <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                    <div className="flex items-center gap-3">
                      {/* Links externos */}
                      <ExternalLinkButton
                        kind="portfolio"
                        url={freelancer.portfolio_url}
                        variant="pill"
                        label="Portfolio"
                        icon={ExternalLink}
                        className="text-blue-700 bg-blue-50 hover:bg-blue-100"
                        iconClassName="h-3 w-3"
                      />

                      <FreelancerLinkedInButton
                        freelancerId={freelancer.id}
                        url={freelancer.linkedin_url}
                        variant="pill"
                        label="LinkedIn"
                        className="text-blue-800 bg-blue-50 hover:bg-blue-100"
                        iconClassName="h-3 w-3"
                      />

                      <div className="text-xs text-slate-500">
                        Confiança: {Math.round(parseFloat(scoring.confidence))}%
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => toggleExpand(freelancer.id)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        {isExpanded ? 'Menos detalhes' : 'Ver detalhes'}
                      </Button>
                      <Button
                        asChild
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <Link to={`/freelancers/${freelancer.id}?fromVaga=${vagaId}`}>
                          <UserPlus className="h-4 w-4 mr-2" />
                          Ver Perfil
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
    </TooltipProvider>
  );
}
